/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css } from "@mui/material"
import { Typography, Box, Button, CircularProgress, Alert } from "@mui/material"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { submitQuestionnaireToGitHub } from "../../../../services/githubService"
import { sendEmail } from "../../../../services/mailService"
import { createNotificationEmailHtml, createCoupleConfirmationEmailHtml } from "../../../../services/notificationEmailService"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    text-align: center;
    max-width: 600px;
    margin: 0 auto;
    padding: 0 1rem;
    
    ${mobileCss(`
        max-width: 90vw;
        gap: 1.5rem;
    `)}
`)

const MessageStyle = withMyTheme((theme) => css`
    font-size: 1.5rem;
    font-weight: 500;
    color: ${theme.palette.text.primary};
    
    ${mobileCss(`
        font-size: 1.2rem;
    `)}
`)

const InfoStyle = withMyTheme((theme) => css`
    font-size: 1.1rem;
    color: ${theme.palette.text.secondary};
    line-height: 1.6;
    max-width: 500px;
    
    ${mobileCss(`
        font-size: 1rem;
    `)}
`)

const ConfirmButtonStyle = withMyTheme((theme) => css`
    padding: 14px 48px;
    font-size: 1.2rem;
    border-radius: 12px;
    text-transform: none;
    font-weight: 600;
    
    ${mobileCss(`
        width: 100%;
        font-size: 1.1rem;
    `)}
`)

const SuccessMessageStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    color: ${theme.palette.success.main};
`)

const IconStyle = withMyTheme((theme) => css`
    font-size: 80px;
`)

const ErrorStyle = withMyTheme((theme) => css`
    margin-top: 1rem;
    max-width: 500px;
`)

interface FinalConfirmationStepProps {
    onStart?: () => void
}

export const FinalConfirmationStep = ({}: FinalConfirmationStepProps) => {
    const { t } = useTranslation()
    const { formData, storageContext } = useQuestionnaireForm()
    const [confirmed, setConfirmed] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [branchName, setBranchName] = useState<string>('')

    const handleConfirm = async () => {
        setLoading(true)
        setError(null)
        
        try {
            console.log('Creating new branch')
            
            // Always create a new branch (no existing branch parameter)
            const branch = await submitQuestionnaireToGitHub(formData, undefined, storageContext)
            setBranchName(branch)
            
            // Send notification email
            try {
                console.log('Sending notification email...')
                const confirmationDate = new Date()
                const dueDate = new Date(confirmationDate)
                dueDate.setDate(dueDate.getDate() + 2) // Add 2 days
                
                const domainValue = formData.domain.type === 'subdomain' 
                    ? formData.domain.subdomainName 
                    : formData.domain.customDomain
                
                const emailHtml = createNotificationEmailHtml(
                    formData.basicInfo.coupleName1,
                    formData.basicInfo.coupleName2,
                    branch,
                    formData.domain.type,
                    domainValue,
                    formData.domain.letUsBuyDomain,
                    confirmationDate,
                    dueDate
                )
                
                await sendEmail({
                    sentTo: 'the.weddie.site@gmail.com',
                    sentFrom: 'noreply@weddie.site',
                    topic: `New Wedding Website Request: ${formData.basicInfo.coupleName1} & ${formData.basicInfo.coupleName2}`,
                    message: `Wedding website request from ${formData.basicInfo.coupleName1} and ${formData.basicInfo.coupleName2}. Branch: ${branch}`,
                    html: emailHtml
                })
                
                console.log('Notification email sent successfully')
            } catch (emailError) {
                console.error('Failed to send notification email:', emailError)
                // Don't fail the whole submission if email fails
            }
            
            // Send confirmation emails to the couple
            try {
                console.log('Sending confirmation emails to the couple...')
                const coupleEmailHtml = createCoupleConfirmationEmailHtml(
                    formData.basicInfo.coupleName1,
                    formData.basicInfo.coupleName2
                )
                
                const emailSubject = `Weddie.Site - ${formData.basicInfo.coupleName1} & ${formData.basicInfo.coupleName2} Wedding Website`
                
                // Send to first contact email if provided
                if (formData.contact.email1) {
                    await sendEmail({
                        sentTo: formData.contact.email1,
                        sentFrom: 'noreply@weddie.site',
                        topic: emailSubject,
                        message: `Thank you for choosing Weddie.Site! Your wedding website will be ready within 48 hours.`,
                        html: coupleEmailHtml
                    })
                    console.log(`Confirmation email sent to ${formData.contact.email1}`)
                }
                
                // Send to second contact email if provided and different from first
                if (formData.contact.email2 && formData.contact.email2 !== formData.contact.email1) {
                    await sendEmail({
                        sentTo: formData.contact.email2,
                        sentFrom: 'noreply@weddie.site',
                        topic: emailSubject,
                        message: `Thank you for choosing Weddie.Site! Your wedding website will be ready within 48 hours.`,
                        html: coupleEmailHtml
                    })
                    console.log(`Confirmation email sent to ${formData.contact.email2}`)
                }
                
                console.log('Couple confirmation emails sent successfully')
            } catch (emailError) {
                console.error('Failed to send confirmation emails to couple:', emailError)
                // Don't fail the whole submission if email fails
            }
            
            setConfirmed(true)
        } catch (err) {
            console.error('Failed to submit questionnaire:', err)
            setError(err instanceof Error ? err.message : 'An unknown error occurred')
        } finally {
            setLoading(false)
        }
    }

    if (confirmed) {
        return (
            <div css={StepContentStyle}>
                <Box css={SuccessMessageStyle}>
                    <CheckCircleOutlineIcon css={IconStyle} />
                    <Typography css={MessageStyle}>
                        {t('questionnaire.confirmation.confirmationSuccess')}
                    </Typography>
                </Box>
            </div>
        )
    }

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.confirmation.title')}
            </Typography>
            
            <Typography css={MessageStyle}>
                {t('questionnaire.confirmation.message')}
            </Typography>

            <Typography css={InfoStyle}>
                {t('questionnaire.confirmation.deliveryInfo')}
            </Typography>

            <Button
                variant="contained"
                size="large"
                onClick={handleConfirm}
                disabled={loading}
                css={ConfirmButtonStyle}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('questionnaire.confirmation.confirmButton')}
            </Button>
            
            {error && (
                <Alert severity="error" css={ErrorStyle}>
                    {error}
                </Alert>
            )}
        </div>
    )
}
